import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toastMessage } from "ui/components";
import {
  fetchCountries,
  getCustomDeviceList,
  getCustomDeviceTypeList,
} from "ui/services";
import { axiosEjFetch } from "ui/services";
import {
  DeviceTypesType,
  MappedDeviceObject,
  newCountryType,
  optionType,
  parentIdOptionType,
  providerDataType,
  reservationType,
  updatedDevice,
} from "../../../../packages/utils/types";

// State interface
interface TempDataStorageState {
  reservations: reservationType[] | null;
  reservationsLoading: boolean;
  reservationsLoaded: boolean;
  providers: parentIdOptionType[] | null;
  allProviders: parentIdOptionType[] | null;
  devices: updatedDevice[] | null;
  deviceTypeOptions: optionType[];
  countries: newCountryType[] | [];
  mappedDevices: MappedDeviceObject | null;
}

// Initial state
const initialState: TempDataStorageState = {
  reservations: null,
  reservationsLoading: false,
  reservationsLoaded: false,
  providers: null,
  allProviders: null,
  devices: null,
  deviceTypeOptions: [],
  countries: [],
  mappedDevices: null,
};

export type ReservationFilters = {
  eventStartFrom?: string | null;
  eventStartTo?: string | null;
  reservationCreatedFrom?: string | null;
  reservationCreatedTo?: string | null;
  includeMediaInfo?: boolean;
};



export const fetchReservations = createAsyncThunk(
  "tempDataStorage/fetchReservations",
  async (args?: ReservationFilters | boolean) => {

    const filters: ReservationFilters =
      typeof args === "boolean" || args == null
        ? {
            includeMediaInfo: typeof args === "boolean" ? args : undefined,
          }
        : args;

    const params = new URLSearchParams();
    params.set("includeMediaInfo", String(filters.includeMediaInfo === true));
    if (filters.eventStartFrom) params.set("eventStartFrom", filters.eventStartFrom);
    if (filters.eventStartTo) params.set("eventStartTo", filters.eventStartTo);
    if (filters.reservationCreatedFrom)
      params.set("reservationCreatedFrom", filters.reservationCreatedFrom);
    if (filters.reservationCreatedTo)
      params.set("reservationCreatedTo", filters.reservationCreatedTo);

    try {
      const response = await axiosEjFetch.get(`/api/reservation?${params.toString()}`);
      return response;
    } catch (error) {
      toastMessage.error("Toast.errorGetReservation");
      toastMessage.error((error as Error).message);
    }
  }
);

export const fetchMyReservations = createAsyncThunk(
  "tempDataStorage/fetchMyReservations",
  async (includeMediaInfo?: boolean) => {
    try {
      const response = await axiosEjFetch.get(
        `/api/reservation/myReservation?includeMediaInfo=${includeMediaInfo === true}`
      );
      return response;
    } catch (error) {
      toastMessage.error("Toast.errorGetReservation");
      toastMessage.error((error as Error).message);
    }
  }
);

const getMappedProviders = async (providerId?: string) => {
  const query = providerId ? `?id=${providerId}` : "";
  const providerResp = await axiosEjFetch.get(`/api/provider${query}`);
  return providerResp.map((item: providerDataType) => ({
    label: item.name,
    value: item.id,
    parentId: item.parentId,
  }));
};

export const fetchProviders = createAsyncThunk(
  "tempDataStorage/fetchProviders",
  async (providerId?: string) => {
    return getMappedProviders(providerId);
  }
);

export const fetchAllProviders = createAsyncThunk(
  "tempDataStorage/fetchAllProviders",
  async () => {
    return getMappedProviders("");
  }
);

export const fetchAllCountries = createAsyncThunk(
  "tempDataStorage/fetchAllCountries",
  async () => {
    try {
      const response = await fetchCountries();
      return response;
    } catch (error) {
      toastMessage.error("Toast.errorGetCountries");
      toastMessage.error((error as Error).message);
    }
  }
);

export const fetchDevices = createAsyncThunk(
  "tempDataStorage/fetchDevices",
  async (args: { providerId?: string; getMappedDevices?: boolean }) => {
    try {
      const mappedProviders: parentIdOptionType[] = await getMappedProviders(
        args.providerId
      );
      const devicesResp = await Promise.all(
        mappedProviders.map(async (item) => {
          const res: DeviceTypesType[] = await getCustomDeviceTypeList(
            item.value
          );
          return res.map((device) => ({ ...device, providerId: item.value }));
        })
      );
      const deviceTypes = devicesResp.flat().map((item) => ({
        label: item.name,
        value: item.id,
        providerId: item.providerId,
      }));
      const deviceTypeOptions = Array.from(
        deviceTypes
          .reduce((map, item) => {
            if (
              !map.has(item.value) ||
              map.get(item.value).providerId !== args.providerId
            ) {
              map.set(item.value, item);
            }
            return map;
          }, new Map())
          .values()
      );
      const resp = await Promise.all(
        mappedProviders.map(async (providerIdItem) => {
          const res: updatedDevice[] = await getCustomDeviceList(
            providerIdItem.value
          );
          res.map((item) => {
            item.providerName = providerIdItem.label;
            item.providerId = providerIdItem.value;
            item.activeReservation = {
              label: item.activeReservationName,
              value: item.activeReservationId,
            };
            item.defaultReservation = {
              label: item.defaultReservationName,
              value: item.defaultReservationId,
            };
            return item;
          });
          return res;
        })
      );
      const devices = resp.flat();
      let mappedDevicesObj: MappedDeviceObject = {};
      if (args.getMappedDevices) {
        mappedDevicesObj = devices.reduce<MappedDeviceObject>((acc, item) => {
          acc[item.internalId] = {
            isActiveReservation: item.isActiveReservation,
            activeReservationId: item.activeReservationId,
            defaultReservationId: item.defaultReservationId,
          };
          return acc;
        }, {});
      }
      return {
        devices,
        deviceTypeOptions,
        mappedDevices: mappedDevicesObj,
      } as any;
    } catch (error) {
      toastMessage.error("errorGetDevices");
      toastMessage.error((error as Error).message);
    }
  }
);

const tempDataStorageSlice = createSlice({
  name: "tempDataStorage",
  initialState,
  reducers: {
    updateDevices: (state, action: PayloadAction<updatedDevice[]>) => {
      console.log({ action });
      state.devices = action.payload;
    },
    clearReservations: (state) => {
      state.reservations = [];
      state.reservationsLoaded = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchReservations.pending, (state) => {
      state.reservationsLoading = true;
    });
    builder.addCase(
      fetchReservations.fulfilled,
      (state, action: PayloadAction<reservationType[]>) => {
        state.reservations = action.payload;
        state.reservationsLoading = false;
        state.reservationsLoaded = true;
      }
    );
    builder.addCase(fetchReservations.rejected, (state) => {
      state.reservationsLoading = false;
    });
    builder.addCase(fetchMyReservations.pending, (state) => {
      state.reservationsLoading = true;
    });
    builder.addCase(
      fetchMyReservations.fulfilled,
      (state, action: PayloadAction<reservationType[]>) => {
        const newData = action.payload;

        // If current data and new data are both empty arrays, skip update
        if (
          Array.isArray(state.reservations) &&
          Array.isArray(newData) &&
          state.reservations.length === 0 &&
          newData.length === 0
        ) {
          state.reservationsLoading = false;
          state.reservationsLoaded = true;
          return;
        }

        state.reservations = action.payload;
        state.reservationsLoading = false;
        state.reservationsLoaded = true;
      }
    );
    builder.addCase(fetchMyReservations.rejected, (state) => {
      state.reservationsLoading = false;
    });
    builder.addCase(
      fetchProviders.fulfilled,
      (state, action: PayloadAction<parentIdOptionType[]>) => {
        state.providers = action.payload;
      }
    );
    builder.addCase(
      fetchAllProviders.fulfilled,
      (state, action: PayloadAction<parentIdOptionType[]>) => {
        state.allProviders = action.payload;
      }
    );
    builder.addCase(
      fetchAllCountries.fulfilled,
      (state, action: PayloadAction<newCountryType[]>) => {
        state.countries = action.payload;
      }
    );
    builder.addCase(
      fetchDevices.fulfilled,
      (
        state,
        action: PayloadAction<{
          devices: updatedDevice[] | null;
          deviceTypeOptions: optionType[];
          mappedDevices: MappedDeviceObject | null;
        }>
      ) => {
        state.devices = action.payload.devices;
        state.deviceTypeOptions = action.payload.deviceTypeOptions;
        state.mappedDevices = action.payload.mappedDevices;
      }
    );
  },
});

export const { updateDevices, clearReservations } = tempDataStorageSlice.actions;

export default tempDataStorageSlice.reducer;
